import React, { FunctionComponent } from 'react';
import { LazyImage } from 'react-lazy-images';
import Person from '@clearkit/icons/glyphs/Person';
import classNames from 'classnames';

import { getInitials } from '../../common/utils';
import { Enum } from '../../common/types';

export const CKAvatarVariant = Enum('person', 'user');
export type CKAvatarVariant = Enum<typeof CKAvatarVariant>;

export interface CKAvatarProps {
  size?: number;
  src?: string;
  name?: string;
  color?: string;
  className?: string;
  alt?: string;
  variant?: CKAvatarVariant;
}

interface PlaceholderProps {
  name?: string;
  isUser?: boolean;
}

const Placeholder: React.FC<PlaceholderProps> = ({ name, isUser = false }) => {
  const initials = name ? getInitials(name) : '?';

  return (
    <React.Fragment>
      {!isUser && <Person />}
      {isUser && <span>{initials || '?'}</span>}
    </React.Fragment>
  );
};

interface AvatarWrapProps extends Pick<CKAvatarProps, 'className' | 'size'> {
  avatarClass: string;
  backgroundColor: string;
}

const AvatarWrap: React.FC<AvatarWrapProps> = ({
  children,
  className,
  avatarClass,
  size,
  backgroundColor,
}) => {
  return (
    <div
      className={className}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `100%`,
      }}
    >
      <div
        className={`ck-avatar ${avatarClass}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          fontSize: `${size}px`,
          backgroundColor,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const CKAvatar: FunctionComponent<CKAvatarProps> = ({
  src,
  size = 32,
  alt,
  name,
  color,
  variant = CKAvatarVariant.person,
  className,
}) => {
  const isUser = variant === 'user';
  const avatarClass = classNames(`ck-avatar--${variant}`, className);

  const fallbackColor = color ? `var(--${color}-300)` : 'var(--blue-300)';
  const backgroundColor = isUser ? fallbackColor : `transparent`;

  if (!src) {
    return (
      <AvatarWrap
        className={className}
        backgroundColor={backgroundColor}
        size={size}
        avatarClass={avatarClass}
      >
        <Placeholder isUser={isUser} name={name} />
      </AvatarWrap>
    );
  }

  return (
    <AvatarWrap
      className={className}
      backgroundColor={backgroundColor}
      size={size}
      avatarClass={avatarClass}
    >
      <LazyImage
        src={src}
        alt={alt}
        placeholder={({ ref }) => <div ref={ref}></div>}
        error={() => <Placeholder isUser={isUser} name={name} />}
        actual={({ imageProps }) => (
          <React.Fragment>
            <img
              alt={alt}
              style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '100%',
              }}
              {...imageProps}
            />
          </React.Fragment>
        )}
      />
    </AvatarWrap>
  );
};
